import axios from 'axios';
import router from '@/router';
import store from '@/store';
import { Message } from 'view-ui-plus';

// Create axios instance
const api = axios.create({
    baseURL: '',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Backward-compatible textbook versions API.
// Some deployments expose only /api/admin/textbook-versions.
export const getTextbookVersions = async () => {
    const endpoints = ['/api/textbook-versions', '/api/admin/textbook-versions'];
    let lastError = null;

    for (const endpoint of endpoints) {
        try {
            return await api.get(endpoint);
        } catch (error) {
            if (error?.response?.status !== 404) {
                throw error;
            }
            lastError = error;
        }
    }

    throw lastError || new Error('Failed to fetch textbook versions');
};

// Request interceptor - add token to requests
api.interceptors.request.use(
    (config) => {
        const token = store.state.token || localStorage.getItem('token');
        // console.log('[API Interceptor] Token from localStorage:', token ? 'exists' : 'null', 'URL:', config.url);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor - handle 401 errors
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response) {
            const status = error.response.status;
            const data = error.response.data;

            if (status === 401) {
                // Clear stored data via store
                store.commit('CLEAR_AUTH');

                // Redirect to login page
                if (router.currentRoute.value.name !== 'Login') {
                    router.push({ name: 'Login', query: { redirect: router.currentRoute.value.fullPath } });
                }
            } else if (status === 403) {
                if (data && data.code === 'USER_BANNED') {
                    // Redirect to banned page
                    if (router.currentRoute.value.name !== 'Banned') {
                        router.push({ name: 'Banned' });
                    }
                } else {
                    Message.error(data.error || '您没有权限执行此操作');
                }
            } else if (status >= 500) {
                Message.error('服务器繁忙，请稍后再试');
            } else {
                // Show other errors unless they are handled by components
                if (data && data.error) {
                    Message.error(data.error);
                }
            }
        } else if (error.request) {
            // Network error
            Message.error('网络连接异常，请检查您的网络');
        } else {
            Message.error('发生未知错误');
        }
        return Promise.reject(error);
    }
);

// Helper functions for auth management
export const setAuthToken = (token) => {
    store.commit('SET_TOKEN', token);
};

export const setUser = (user) => {
    store.commit('SET_USER', user);
};

export const getUser = () => {
    return store.state.user;
};

export const getToken = () => {
    return store.state.token;
};

export const isAuthenticated = () => {
    return !!store.state.token;
};

export const logout = () => {
    store.commit('CLEAR_AUTH');
    router.push({ name: 'Login' });
};

// -- OSS API --
export const uploadOSSFile = (formData) => api.post('/api/admin/oss/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});

export const getAlbums = () => api.get('/api/albums');
export const getAlbumDetail = (id) => api.get(`/api/albums/${id}`);
export const createAlbum = (data) => api.post('/api/admin/albums', data);
export const updateAlbum = (id, data) => api.put(`/api/admin/albums/${id}`, data);
export const deleteAlbum = (id) => api.delete(`/api/admin/albums/${id}`);
export const addAlbumNode = (albumId, data) => api.post(`/api/admin/albums/${albumId}/nodes`, data);
export const updateAlbumNode = (nodeId, data) => api.put(`/api/admin/albums/nodes/${nodeId}`, data);
export const deleteAlbumNode = (nodeId) => api.delete(`/api/admin/albums/nodes/${nodeId}`);
export const getRelatedAlbums = (id) => api.get(`/api/albums/${id}/related`);
// export const batchAddPapersToBasket = (data) => api.post('/api/basket/batch-add-papers', data);
export const batchDownloadPapers = (data) => api.post('/api/papers/batch-download', data, { responseType: 'blob' });
export const downloadPaperPpt = (data) => api.post('/api/downloadPpt', data, { responseType: 'blob' });
export const downloadDocx = (data) => api.post('/api/downloadDocx', data, { responseType: 'blob' });

export default api;
